// Products — the per-company catalog, wired to /v1/companies/:id/products.
//
// The create/edit form used to live here, which is why the top-level Products
// PIM could only link to this page rather than edit anything. It now lives in
// components/ProductModal and both pages share it; this page keeps the
// company-scoped table and the resolve-backed price preview.
//
// Reached by deep-link from CompanyDetail (or the company picker at the top of
// this page for direct nav). Money is bigint minor units end-to-end:
// unit_rate_minor is parsed from a major-unit input to bigint on save, and
// displayed via fmtCcyMinor. The "Price preview" panel exercises the resolve
// endpoint (POST …/products/resolve) which returns bigint minor totals.
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { StatusPill } from "@/components/UI";
import { ProductModal } from "@/components/ProductModal";
import { ConfirmDialog, type ConfirmRequest } from "@/components/ConfirmDialog";
import { useUI } from "@/store/ui";
import { fmtCcyMinor } from "@/utils/format";
import { apiErrorMessage } from "@/utils/apiError";
import { useCompanies } from "@/queries/useCompanies";
import { useCatalogs } from "@/queries/useCatalogs";
import { useProducts } from "@/queries/useProducts";
import {
  deleteProduct,
  resolveProducts,
  type Product,
  type ResolveResult,
} from "@/api/products";

// ─── Price preview (resolve) ─────────────────────────────────────────────────

function PricePreview({
  companyId,
  products,
}: {
  companyId: string;
  products: Product[];
}) {
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState("1");
  const [result, setResult] = useState<ResolveResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      resolveProducts(companyId, [
        { item_id: itemId, qty: parseInt(qty, 10) || 0 },
      ]),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
    },
    onError: (err) => {
      setResult(null);
      setError(apiErrorMessage(err, "Could not price these items."));
    },
  });

  const effectiveItem = itemId || products[0]?.id || "";

  return (
    <div className="card" style={{ padding: 16, marginTop: "var(--card-gap, 16px)" }}>
      <h3 style={{ marginBottom: 12 }}>Price preview</h3>
      <p className="dim" style={{ fontSize: 12, marginBottom: 12 }}>
        Prices a line via <span className="mono">POST …/products/resolve</span>{" "}
        (bigint minor totals).
      </p>
      <div className="row gap-12" style={{ alignItems: "flex-end" }}>
        <div className="field" style={{ flex: 1 }}>
          <label className="label" htmlFor="pp-item">
            Product
          </label>
          <select
            id="pp-item"
            className="input"
            value={effectiveItem}
            onChange={(e) => setItemId(e.target.value)}
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ width: 100 }}>
          <label className="label" htmlFor="pp-qty">
            Qty
          </label>
          <input
            id="pp-qty"
            className="input"
            type="number"
            min={1}
            step={1}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </div>
        <button
          className="btn"
          type="button"
          disabled={mutation.isPending || !effectiveItem}
          onClick={() => {
            if (!itemId && products[0]) setItemId(products[0].id);
            mutation.mutate();
          }}
        >
          {mutation.isPending ? "Pricing…" : "Preview"}
        </button>
      </div>

      {error && (
        <div style={{ color: "var(--neg)", fontSize: 12, marginTop: 12 }}>
          {error}
        </div>
      )}
      {result && (
        <div
          className="stack-6"
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: "1px solid var(--hair)",
          }}
        >
          <div className="row" style={{ justifyContent: "space-between" }}>
            <span className="dim">Subtotal</span>
            <span className="mono">
              {fmtCcyMinor(result.subtotal_minor, result.currency)}
            </span>
          </div>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <span className="dim">Tax</span>
            <span className="mono">
              {fmtCcyMinor(result.tax_minor, result.currency)}
            </span>
          </div>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <strong>Total</strong>
            <strong className="mono">
              {fmtCcyMinor(result.total_minor, result.currency)}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function Products() {
  const { id: companyId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: companies = [] } = useCompanies();
  const { data: catalogs = [] } = useCatalogs(companyId);
  const {
    data: products = [],
    isLoading,
    isError,
  } = useProducts(companyId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const addToast = useUI((s) => s.addToast);

  const company = useMemo(
    () => companies.find((c) => c.id === companyId),
    [companies, companyId],
  );

  const deleteMutation = useMutation({
    mutationFn: (productId: string) => deleteProduct(productId),
    onSuccess: () => {
      setConfirm(null);
      addToast({ title: "Product deleted" });
      return qc.invalidateQueries({ queryKey: ["products", companyId] });
    },
    onError: () => setConfirm(null),
  });

  if (!companyId) {
    return (
      <div className="page">
        <h1>Products</h1>
        <p className="dim">No company selected.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <Link to={`/companies/${companyId}`} className="btn ghost sm">
        <I.ChevronLeft size={12} /> Back to company
      </Link>

      <div className="page-head" style={{ marginTop: 12 }}>
        <div>
          <div className="eyebrow">Catalog</div>
          <h1>Products</h1>
          <div className="sub">
            {company ? company.display_name : companyId} ·{" "}
            {isLoading ? "loading…" : `${products.length} products`}
          </div>
        </div>
        <div className="actions">
          {/* Company picker for direct nav between catalogs. */}
          <select
            className="input"
            style={{ maxWidth: 220 }}
            value={companyId}
            onChange={(e) => navigate(`/companies/${e.target.value}/products`)}
            aria-label="Switch company"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
          <button
            className="btn accent"
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
          >
            <I.Plus size={12} /> New product
          </button>
        </div>
      </div>

      <ConfirmDialog
        request={confirm}
        busy={deleteMutation.isPending}
        onClose={() => setConfirm(null)}
      />

      <ProductModal
        companyId={companyId}
        product={editing}
        catalogs={catalogs}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />

      <div className="card">
        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading products…
            </div>
          </div>
        ) : isError ? (
          <div className="empty">
            <div className="icon">
              <I.Wallet size={16} />
            </div>
            <h3>Couldn't load products</h3>
            <p>The request to the go-pim admin API failed. Try again shortly.</p>
          </div>
        ) : products.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Wallet size={16} />
            </div>
            <h3>No products yet</h3>
            <p>Add the first product to this company's catalog.</p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Product</th>
                <th>Type</th>
                <th>Unit rate</th>
                <th>Tax</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="cell-name">{p.name}</div>
                    <div className="cell-id dim">
                      {p.category || "—"}
                      {p.hsn_sac ? ` · HSN ${p.hsn_sac}` : ""}
                    </div>
                  </td>
                  <td className="dim">{p.business_type}</td>
                  <td className="mono">
                    {fmtCcyMinor(p.unit_rate_minor, p.currency)}
                    {p.unit ? <span className="dim"> / {p.unit}</span> : null}
                  </td>
                  <td className="dim">{p.tax_scheme}</td>
                  <td>
                    <StatusPill status={p.active ? "active" : "retired"} />
                  </td>
                  <td className="right">
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        className="btn ghost sm"
                        type="button"
                        onClick={() => {
                          setEditing(p);
                          setModalOpen(true);
                        }}
                      >
                        <I.Edit size={11} /> Edit
                      </button>
                      <button
                        className="btn ghost sm"
                        type="button"
                        disabled={deleteMutation.isPending}
                        onClick={() =>
                          setConfirm({
                            title: `Delete "${p.name}"?`,
                            body: "This cannot be undone. Documents already issued keep their frozen copy of this product, so past receipts and invoices are unaffected.",
                            confirmLabel: "Delete product",
                            destructive: true,
                            onConfirm: () => deleteMutation.mutate(p.id),
                          })
                        }
                      >
                        <I.Trash size={11} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {products.length > 0 && (
        <PricePreview companyId={companyId} products={products} />
      )}
    </div>
  );
}
